import React from 'react';
import { FiExternalLink, FiArrowRight, FiGlobe } from 'react-icons/fi';
import './FeedNewsList.css';

const fallbackFormatRelativeTime = iso => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString('es-MX', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const shortenContent = (text, maxLength = 260) => {
  const trimmed = String(text || '').trim();
  if (!trimmed) return '';
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength - 1).trim()}…`;
};

const normalizeUrl = value => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

const extractHost = url => {
  if (!url) return '';
  try {
    const { hostname } = new URL(url);
    return hostname.replace(/^www\./i, '');
  } catch {
    return '';
  }
};

const EXIT_WARNING_MESSAGE =
  'Estas por salir de Flotte para consultar una noticia externa. ¿Deseas continuar?';

export default function FeedNewsList({
  items = [],
  loading = false,
  error = '',
  formatRelativeTime,
  onOpenCompany,
  onViewAllNews,
}) {
  const hasItems = Array.isArray(items) && items.length > 0;
  const formatTime = React.useCallback(
    value => {
      if (typeof formatRelativeTime === 'function') {
        return formatRelativeTime(value);
      }
      return fallbackFormatRelativeTime(value);
    },
    [formatRelativeTime]
  );

  const handleCompanyClick = company => {
    if (typeof onOpenCompany === 'function') {
      onOpenCompany(company);
    }
  };

  const handleOpenExternal = link => {
    const normalized = normalizeUrl(link);
    if (!normalized) return;
    const confirmed = window.confirm(EXIT_WARNING_MESSAGE);
    if (!confirmed) return;
    window.open(normalized, '_blank', 'noopener,noreferrer');
  };

  const handleViewAll = () => {
    if (typeof onViewAllNews === 'function') {
      onViewAllNews();
    }
  };

  return (
    <section className="feed-news feed-card feed-spotlight-card" aria-label="Noticias destacadas">
      <header className="feed-news__header">
        <div className="feed-news__heading">
          <h3>NOTICIAS</h3>
          {typeof onViewAllNews === 'function' ? (
            <button type="button" className="feed-news__view-link" onClick={handleViewAll}>
              Ver todas
              <FiArrowRight aria-hidden="true" />
            </button>
          ) : null}
        </div>
        {loading && !hasItems ? <span className="feed-news__status">Cargando…</span> : null}
      </header>
      <div className="feed-news__body">
        {loading && !hasItems ? (
          <ul className="feed-news__skeleton-list" aria-hidden="true">
            {[0, 1, 2].map(key => (
              <li key={key} className="feed-news__skeleton-item">
                <div className="feed-news__skeleton-avatar" />
                <div className="feed-news__skeleton-lines">
                  <span />
                  <span />
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        {!loading && !hasItems ? (
          <p className={`feed-news__empty${error ? ' feed-news__empty--error' : ''}`}>
            {error ? 'No pudimos cargar las noticias.' : 'No hay noticias disponibles por ahora.'}
          </p>
        ) : null}

        {hasItems ? (
          <ul className="feed-news__list">
            {items.map((item, index) => {
              const creationDate = item.created || item.createdAt || '';
              const key = item.id || creationDate || `news-${index}`;
              const companyName = item.company?.name || 'Actualizacion';
              const timestamp = formatTime(creationDate);
              const content = shortenContent(item.content || '', 260);
              const logo = item.company?.logoUrl || '';
              const initials = companyName.trim().slice(0, 1).toUpperCase() || 'N';
              const link = typeof item.newsLink === 'string' ? item.newsLink.trim() : '';
              const normalizedLink = normalizeUrl(link);
              const host = extractHost(normalizedLink);

              return (
                <li key={key} className="feed-news__item">
                  <button
                    type="button"
                    className={`feed-news__avatar${logo ? ' feed-news__avatar--image' : ' feed-news__avatar--icon'}`}
                    aria-label={`Ver perfil de ${companyName}`}
                    onClick={() => handleCompanyClick(item.company)}
                  >
                    {logo ? <img src={logo} alt="" /> : <FiGlobe aria-hidden="true" />}
                  </button>
                  <div className="feed-news__content">
                    <div className="feed-news__meta">
                      <button
                        type="button"
                        className="feed-news__company"
                        onClick={() => handleCompanyClick(item.company)}
                      >
                        {companyName || initials}
                      </button>
                      {timestamp ? (
                        <>
                          <span className="feed-news__meta-divider" aria-hidden="true">
                            •
                          </span>
                          <time dateTime={creationDate}>{timestamp}</time>
                        </>
                      ) : null}
                    </div>
                    {content ? (
                      <button
                        type="button"
                        className="feed-news__summary"
                        onClick={() => handleOpenExternal(normalizedLink)}
                        title="Abrir noticia externa"
                      >
                        {content}
                      </button>
                    ) : null}
                    <div className="feed-news__actions">
                      {host ? <span className="feed-news__host">{host}</span> : null}
                      {normalizedLink ? (
                        <button
                          type="button"
                          className="feed-news__open"
                          onClick={() => handleOpenExternal(normalizedLink)}
                        >
                          <FiExternalLink aria-hidden="true" />
                          Abrir noticia
                        </button>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}

        {error && hasItems ? (
          <p className="feed-news__footnote" role="status">
            Hubo problemas al actualizar las noticias recientes.
          </p>
        ) : null}
        {!loading && hasItems && typeof onViewAllNews === 'function' ? (
          <div className="feed-news__footer">
            <button type="button" className="feed-news__view-all" onClick={handleViewAll}>
              Ver más noticias
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
